import { useState } from "react";
import { useEffect } from "react";
import { toast } from "sonner";
import { ConfirmButton } from "./ConfirmButton";

const card = "rounded-2xl border border-[#2E2E2E] bg-[#111111] p-6 space-y-4 max-w-3xl";
const field = "rounded-xl bg-[#050505] border border-[#2E2E2E] px-4 py-3 text-[15px] font-medium text-white placeholder:text-[#D6D6D6] outline-none focus:border-[#FF5C5C] w-full";

const DrillCard = ({ api, authHeaders }) => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    fetch(`${api}/admin/drill/latest`, { headers: authHeaders })
      .then((r) => r.json()).then(setState).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!state?.running) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [state?.running]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${api}/admin/drill/run`, { method: "POST", headers: authHeaders });
      const d = await r.json();
      if (d.already_running) toast.info("The drill is already running.");
      else toast.success("Drill started — 12 questions, takes a couple of minutes. Results land here and alert you if anything slips.");
      setTimeout(load, 3000);
    } catch {
      toast.error("Couldn't start the drill.");
    }
    setBusy(false);
  };

  const latest = state?.latest;
  return (
    <div className={card} data-testid="drill-card">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-base font-bold text-white">Weekly legal drill</p>
        {state?.running && <span className="text-[15px] font-bold text-[#FF5C5C]">Running now...</span>}
        <button onClick={run} disabled={busy || state?.running} data-testid="drill-run-button"
          className="ml-auto text-[15px] font-semibold rounded-xl bg-white text-black px-4 py-2.5 hover:bg-[#D6D6D6] disabled:opacity-40 transition-colors duration-150">
          Run drill now
        </button>
      </div>
      <p className="text-[15px] font-medium text-[#D6D6D6]">
        Every Monday at 6:30 AM Arizona, the system fires 12 adversarial legal questions at the chat agent and checks
        every answer declines a verdict and routes to counsel. If anything slips, you get an alert and an email.
      </p>
      {latest ? (
        <div data-testid="drill-latest">
          <p className={`text-[15px] font-bold ${latest.ok ? "text-[#4ADE80]" : "text-[#FF5C5C]"}`}>
            Last run: {latest.passed}/{latest.total} passed · {new Date(latest.ran_at).toLocaleString()} · {latest.trigger}
          </p>
          {!latest.ok && (
            <div className="mt-2 space-y-2">
              {latest.failures.map((f) => (
                <div key={f.key} className="rounded-xl border border-[#2E2E2E] bg-[#050505] p-3">
                  <p className="text-[15px] font-bold text-[#FF5C5C]">{f.key} — {f.fails.join(", ")}</p>
                  <p className="text-[15px] font-medium text-[#D6D6D6] mt-1">"{f.snippet}"</p>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="text-[15px] font-medium text-[#D6D6D6]">No runs yet — the first scheduled run is Monday morning, or run it now.</p>
      )}
    </div>
  );
};

const WinbackCard = ({ api, authHeaders }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => {
    fetch(`${api}/admin/winback/preview`, { headers: authHeaders })
      .then((r) => r.json()).then(setPreview).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${api}/admin/winback/send`, { method: "POST", headers: authHeaders });
      const d = await r.json();
      setResult(d);
      if (d.sent) toast.success(`Nudge sent to ${d.sent} lead(s).`);
      else toast.info("Nobody eligible right now.");
      load();
    } catch { toast.error("That didn't go through."); }
    setBusy(false);
  };

  return (
    <div className={card} data-testid="winback-card">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-base font-bold text-white">SMS win-back nudge</p>
        {preview && <span className="ml-auto text-[15px] font-medium text-[#D6D6D6]" data-testid="winback-count">{preview.count} eligible</span>}
      </div>
      <p className="text-[15px] font-medium text-[#D6D6D6]">
        One polite text to leads who enquired, gave SMS consent, and then went quiet for 5 or more days.
        Each lead only ever gets one nudge, opted-out numbers are skipped automatically, and their replies
        arrive in your email.
      </p>
      {preview && preview.count > 0 && (
        <div className="border-t border-[#2E2E2E] pt-3 space-y-1.5">
          {preview.eligible.map((p, i) => (
            <p key={i} className="text-[15px] font-medium text-[#D6D6D6]" data-testid="winback-eligible-row">
              <span className="text-white font-semibold">{p.name}</span> · {p.phone} · enquired {(p.enquiry_at || "").slice(0, 10)}
            </p>
          ))}
        </div>
      )}
      {preview && preview.count === 0 && (
        <p className="text-[15px] font-medium text-[#D6D6D6]" data-testid="winback-empty">
          Nobody is eligible right now — every consented lead is either fresh, booked, or already nudged.
        </p>
      )}
      {preview && preview.count > 0 && (
        <ConfirmButton onConfirm={send} testId="winback-send-button"
          confirmLabel={`Sure? Text ${preview.count} lead(s) now`}
          className="rounded-xl bg-white text-black font-semibold text-[15px] px-5 py-3 hover:bg-[#D6D6D6] transition-colors duration-150"
          confirmClassName="rounded-xl bg-[#DC2626] text-white font-semibold text-[15px] px-5 py-3">
          {busy ? "Sending..." : "Send the nudge now"}
        </ConfirmButton>
      )}
      {result && result.sent > 0 && (
        <p className="text-[15px] font-bold text-[#4ADE80]" data-testid="winback-result">
          Sent to {result.sent} lead(s) — replies will arrive in your email.
        </p>
      )}
    </div>
  );
};

export const AutomationView = ({ webhooks, settings, api, authHeaders, onChanged }) => {
  const [form, setForm] = useState({ url: "", label: "", events: [] });
  const [retention, setRetention] = useState(null);
  const [busy, setBusy] = useState(false);
  const [calendar, setCalendar] = useState(null);
  useEffect(() => {
    fetch(`${api}/admin/calendar`, { headers: authHeaders })
      .then((r) => r.json()).then(setCalendar).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);
  if (!webhooks || !settings) return <p className="text-[17px] font-medium text-[#D6D6D6]">Loading...</p>;

  const jsonHeaders = { "Content-Type": "application/json", ...authHeaders };

  const saveSettings = async (changes) => {
    const res = await fetch(`${api}/admin/settings`, { method: "PUT", headers: jsonHeaders, body: JSON.stringify(changes) });
    if (res.ok) { toast.success("Saved."); onChanged(); }
    else toast.error("That didn't save.");
  };

  const addHook = async (e) => {
    e.preventDefault();
    if (!form.url.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`${api}/admin/webhooks`, { method: "POST", headers: jsonHeaders, body: JSON.stringify(form) });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "That didn't go through.");
      toast.success("Webhook added.");
      setForm({ url: "", label: "", events: [] });
      onChanged();
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  const removeHook = async (id) => {
    const res = await fetch(`${api}/admin/webhooks/${id}`, { method: "DELETE", headers: authHeaders });
    if (res.ok) { toast.success("Webhook removed."); onChanged(); }
    else toast.error("That didn't go through.");
  };

  const run = async (path, describe) => {
    const res = await fetch(`${api}${path}`, { method: "POST", headers: authHeaders });
    const d = await res.json().catch(() => ({}));
    if (res.ok) { toast.success(describe(d)); onChanged(); }
    else toast.error("That didn't go through.");
  };

  const toggleEvent = (ev) => setForm((f) => ({
    ...f, events: f.events.includes(ev) ? f.events.filter((x) => x !== ev) : [...f.events, ev],
  }));

  return (
    <div className="space-y-6" data-testid="automation-view">
      <div className={card} data-testid="calendar-card">
        <div className="flex items-center gap-2">
          <p className="text-base font-bold text-white">Your calendar feed</p>
          {calendar && <span className="ml-auto text-[15px] font-medium text-[#D6D6D6]">{calendar.upcoming} upcoming call(s)</span>}
        </div>
        <p className="text-[15px] font-medium text-[#D6D6D6]">
          Paste this once into Google Calendar — Other calendars → From URL — and every booked call shows up
          automatically with reminders built in. Moves and cancellations update on their own.
        </p>
        {calendar ? (
          <div className="flex items-center gap-2">
            <input readOnly value={calendar.feed_url} data-testid="calendar-feed-url"
              onFocus={(e) => e.target.select()}
              className="rounded-xl bg-[#050505] border border-[#2E2E2E] px-4 py-3 text-[15px] font-medium text-[#D6D6D6] outline-none w-full" />
            <button onClick={() => { navigator.clipboard.writeText(calendar.feed_url); toast.success("Copied."); }}
              data-testid="calendar-copy-button"
              className="flex items-center gap-1.5 rounded-xl border border-[#2E2E2E] text-[#D6D6D6] font-semibold text-[15px] px-4 py-3 hover:bg-white hover:text-black shrink-0 transition-colors duration-150">
              Copy
            </button>
          </div>
        ) : (
          <p className="text-[15px] font-medium text-[#D6D6D6]">Loading the feed link...</p>
        )}
        <p className="text-[15px] font-medium text-[#D6D6D6]">
          Everyone who books also gets the invite attached to their confirmation email, plus an "Add to Google Calendar" button on the spot.
        </p>
      </div>

      <div className={card} data-testid="notify-settings-card">
        <p className="text-base font-bold text-white">When someone enquires</p>
        <label className="flex items-center gap-3 text-[15px] font-medium text-[#D6D6D6] cursor-pointer">
          <input type="checkbox" checked={!!settings.notify_email_enabled} data-testid="notify-email-toggle"
            onChange={(e) => saveSettings({ notify_email_enabled: e.target.checked })} className="accent-[#DC2626] w-4 h-4" />
          Email me right away
        </label>
        <label className="flex items-center gap-3 text-[15px] font-medium text-[#D6D6D6] cursor-pointer">
          <input type="checkbox" checked={!!settings.notify_sms_enabled} data-testid="notify-sms-toggle"
            onChange={(e) => saveSettings({ notify_sms_enabled: e.target.checked })} className="accent-[#DC2626] w-4 h-4" />
          Text me right away
        </label>
        <div className="flex items-end gap-3 pt-2 border-t border-[#2E2E2E]">
          <div>
            <p className="text-[15px] font-semibold text-white mb-2">Keep unattached conversations for</p>
            <div className="flex items-center gap-2">
              <input type="number" min={30} className={`${field} w-28`} data-testid="retention-days-input"
                value={retention ?? settings.retention_days} onChange={(e) => setRetention(e.target.value)} />
              <span className="text-[15px] font-medium text-[#D6D6D6]">days</span>
            </div>
          </div>
          <button onClick={() => saveSettings({ retention_days: parseInt(retention ?? settings.retention_days, 10) })} data-testid="retention-save-button"
            className="rounded-xl bg-white text-black font-semibold text-[15px] px-4 py-3 hover:bg-[#D6D6D6] transition-colors duration-150">
            Save
          </button>
        </div>
        <p className="text-[15px] font-medium text-[#D6D6D6]">
          Conversations that never turned into a contact are deleted after this window, every night, and each cleanup lands in the audit log.
        </p>
      </div>

      <div className={card} data-testid="webhooks-card">
        <p className="text-base font-bold text-white">Send events to your CRM or Zapier</p>
        <p className="text-[15px] font-medium text-[#D6D6D6]">
          New enquiries, stage moves, verified contacts, and bookings can post straight to any URL — a Zapier catch hook, a CRM endpoint, anything that takes JSON.
        </p>
        <form onSubmit={addHook} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input className={`${field} sm:col-span-2`} placeholder="https://hooks.zapier.com/..." value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="webhook-url-input" />
            <input className={field} placeholder="Label (optional)" value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="webhook-label-input" />
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {(webhooks.events || []).map((ev) => (
              <label key={ev} className="flex items-center gap-2 text-[15px] font-medium text-[#D6D6D6] cursor-pointer">
                <input type="checkbox" checked={form.events.includes(ev)} onChange={() => toggleEvent(ev)}
                  className="accent-[#DC2626] w-4 h-4" data-testid={`webhook-event-${ev.replace(".", "-")}`} />
                {ev}
              </label>
            ))}
          </div>
          <p className="text-[15px] font-medium text-[#D6D6D6]">Leave everything unchecked to receive every event.</p>
          <button type="submit" disabled={busy || !form.url.trim()} data-testid="webhook-add-button"
            className="rounded-xl bg-white text-black font-semibold text-[15px] px-5 py-3 hover:bg-[#D6D6D6] disabled:opacity-40 transition-colors duration-150">
            {busy ? "Adding..." : "Add webhook"}
          </button>
        </form>
        {(webhooks.webhooks || []).length > 0 && (
          <div className="border-t border-[#2E2E2E] pt-3 space-y-2">
            {webhooks.webhooks.map((h) => (
              <div key={h.id} className="flex items-center gap-3" data-testid="webhook-row">
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-semibold text-white truncate">{h.label || h.url}</p>
                  <p className="text-[15px] font-medium text-[#D6D6D6] truncate">
                    {h.label ? `${h.url} · ` : ""}{(h.events || []).length ? h.events.join(", ") : "every event"}
                  </p>
                </div>
                <ConfirmButton onConfirm={() => removeHook(h.id)} testId="webhook-remove-button" confirmLabel="Sure? Remove it"
                  className="flex items-center gap-1.5 text-[15px] font-semibold text-[#D6D6D6] hover:text-[#FF5C5C] shrink-0 transition-colors duration-150"
                  confirmClassName="flex items-center gap-1.5 text-[15px] font-semibold text-[#FF5C5C] shrink-0">
                  Remove
                </ConfirmButton>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={card} data-testid="sweeps-card">
        <p className="text-base font-bold text-white">Run the nightly jobs now</p>
        <p className="text-[15px] font-medium text-[#D6D6D6]">
          Duplicate merging and retention cleanup run every night on their own. These buttons run them right now.
        </p>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => run("/admin/dedup/run", (d) => d.merged ? `Merged ${d.merged} duplicate contact(s).` : "No duplicates found.")}
            data-testid="run-dedup-button"
            className="rounded-xl border border-[#2E2E2E] text-[#D6D6D6] font-semibold text-[15px] px-4 py-3 hover:bg-white hover:text-black transition-colors duration-150">
            Merge duplicate contacts
          </button>
          <ConfirmButton onConfirm={() => run("/admin/retention/run", (d) => d.sessions ? `Removed ${d.sessions} old conversation(s).` : "Nothing old enough to remove.")}
            testId="run-retention-button" confirmLabel="Sure? This deletes old conversations"
            className="rounded-xl border border-[#2E2E2E] text-[#D6D6D6] font-semibold text-[15px] px-4 py-3 hover:bg-white hover:text-black transition-colors duration-150"
            confirmClassName="rounded-xl border border-[#FF5C5C] text-[#FF5C5C] font-semibold text-[15px] px-4 py-3">
            Run retention cleanup
          </ConfirmButton>
          <button onClick={() => run("/admin/enquiry-reminders/run", (d) => d.reminded ? `${d.reminded} reminder(s) sent.` : "Nothing waiting on you right now.")}
            data-testid="run-enquiry-reminders-button"
            className="rounded-xl border border-[#2E2E2E] text-[#D6D6D6] font-semibold text-[15px] px-4 py-3 hover:bg-white hover:text-black transition-colors duration-150">
            Check unanswered enquiries
          </button>
        </div>
      </div>

      <WinbackCard api={api} authHeaders={authHeaders} />

      <DrillCard api={api} authHeaders={authHeaders} />
    </div>
  );
};
